// src/pages/TeamAccessPage.js
//
// Manager-only page. Two things:
//   1. See each employee's role, status, and the pages their role can reach.
//   2. Edit per-employee access overrides — grant a page their role normally can't open, or
//      block a page their role normally can. Overrides are stored on users/{uid}.pageOverrides
//      ({ [pageKey]: true|false }); an absent key means "use the role default". Enforcement lives
//      in ProtectedRoute + the sidebar (see src/config/pageAccess.js).

import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, query, orderBy, doc, updateDoc, deleteField } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { useSocialLanguage } from '../hooks/useSocialLanguage';
import LanguageToggle from '../components/LanguageToggle';
import {
  CATEGORIES,
  PAGE_CATALOG,
  ALL_ROLES,
  roleLabel,
  roleCanAccess,
  canAccessPage,
} from '../config/pageAccess';
import styles from '../styles/TeamAccessPage.module.css';

const TRANSLATIONS = {
  en: {
    pageTitle: 'Team Access & Permissions',
    subtitle: "See each employee's role and control which pages they can access.",
    noPermission: 'You do not have permission to view this page.',
    loading: 'Loading team...',
    loadFailed: 'Failed to load team members.',
    searchPlaceholder: 'Search by name or email...',
    roleFilter: 'Role:',
    allRoles: 'All Roles',
    statusFilter: 'Status:',
    allStatuses: 'All',
    active: 'Active',
    terminated: 'Fired',
    viewByEmployee: 'By Employee',
    viewByRole: 'Role Reference',
    colAccess: 'Page',
    pagesCount: (n) => `${n} page${n === 1 ? '' : 's'}`,
    editAccess: 'Edit access',
    doneEditing: 'Done',
    showPages: 'Show pages',
    hidePages: 'Hide pages',
    noEmployees: 'No employees match your filters.',
    everyone: 'All signed-in users',
    roleColumnNote: 'A ✓ means that role can open the page by default (before per-employee overrides).',
    noRole: 'No role',
    optDefault: 'Default',
    optAllow: 'Allow',
    optDeny: 'Deny',
    saving: 'Saving...',
    overrideAllowed: 'Allowed (override)',
    overrideDenied: 'Blocked (override)',
    byRoleAllowed: 'Allowed by role',
    byRoleDenied: 'Not allowed by role',
    editHint: 'Set each page to Allow or Deny for this employee, or leave as Default to follow their role.',
    saveFailed: 'Failed to save. Please try again.',
    legendDefault: 'Default = follows role',
  },
  ar: {
    pageTitle: 'صلاحيات ووصول الفريق',
    subtitle: 'شاهد دور كل موظف وتحكّم بالصفحات التي يمكنه الوصول إليها.',
    noPermission: 'ليس لديك صلاحية لعرض هذه الصفحة.',
    loading: 'جارٍ تحميل الفريق...',
    loadFailed: 'فشل تحميل أعضاء الفريق.',
    searchPlaceholder: 'ابحث بالاسم أو البريد الإلكتروني...',
    roleFilter: 'الدور:',
    allRoles: 'كل الأدوار',
    statusFilter: 'الحالة:',
    allStatuses: 'الكل',
    active: 'نشط',
    terminated: 'مفصول',
    viewByEmployee: 'حسب الموظف',
    viewByRole: 'مرجع الأدوار',
    colAccess: 'الصفحة',
    pagesCount: (n) => `${n} صفحة`,
    editAccess: 'تعديل الصلاحيات',
    doneEditing: 'تم',
    showPages: 'عرض الصفحات',
    hidePages: 'إخفاء الصفحات',
    noEmployees: 'لا يوجد موظفون مطابقون للفلاتر.',
    everyone: 'كل المستخدمين المسجّلين',
    roleColumnNote: 'علامة ✓ تعني أن هذا الدور يستطيع فتح الصفحة افتراضياً (قبل التخصيص لكل موظف).',
    noRole: 'بدون دور',
    optDefault: 'افتراضي',
    optAllow: 'سماح',
    optDeny: 'منع',
    saving: 'جارٍ الحفظ...',
    overrideAllowed: 'مسموح (تخصيص)',
    overrideDenied: 'ممنوع (تخصيص)',
    byRoleAllowed: 'مسموح حسب الدور',
    byRoleDenied: 'غير مسموح حسب الدور',
    editHint: 'اضبط كل صفحة على سماح أو منع لهذا الموظف، أو اتركها افتراضي لتتبع دوره.',
    saveFailed: 'فشل الحفظ. حاول مرة أخرى.',
    legendDefault: 'افتراضي = حسب الدور',
  },
};

const isTerminated = (emp) =>
  (emp.employmentStatus || (emp.active === false ? 'Terminated' : 'Active')) === 'Terminated';

const MANAGER_ROLES = ['Manager'];

const TeamAccessPage = () => {
  const { userRole } = useAuth();
  const { language, toggleLanguage, isRTL } = useSocialLanguage();
  const t = TRANSLATIONS[language];
  const canView = MANAGER_ROLES.includes(userRole);

  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active');
  const [view, setView] = useState('employee'); // 'employee' | 'role'
  const [expandedId, setExpandedId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [savingCell, setSavingCell] = useState(null); // `${empId}:${pageKey}`

  useEffect(() => {
    if (!canView) {
      setLoading(false);
      return;
    }
    const fetchUsers = async () => {
      setLoading(true);
      setError('');
      try {
        const usersCol = collection(db, 'users');
        const qUsers = query(usersCol, orderBy('name', 'asc'));
        const snapshot = await getDocs(qUsers);
        setEmployees(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error('Error fetching users:', err);
        setError(t.loadFailed);
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView]);

  const rolesPresent = useMemo(() => {
    const set = new Set(employees.map((e) => e.role).filter(Boolean));
    return ALL_ROLES.filter((r) => set.has(r));
  }, [employees]);

  const filteredEmployees = useMemo(() => {
    const term = search.trim().toLowerCase();
    return employees.filter((emp) => {
      if (roleFilter !== 'all' && emp.role !== roleFilter) return false;
      const terminated = isTerminated(emp);
      if (statusFilter === 'active' && terminated) return false;
      if (statusFilter === 'terminated' && !terminated) return false;
      if (term) {
        const hay = `${emp.name || ''} ${emp.email || ''}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return true;
    });
  }, [employees, search, roleFilter, statusFilter]);

  const accessiblePagesFor = (emp) =>
    PAGE_CATALOG.filter((p) => canAccessPage(p.key, emp.role || '', emp.pageOverrides));

  const groupByCategory = (pages) =>
    Object.keys(CATEGORIES)
      .map((catKey) => ({ catKey, items: pages.filter((p) => p.category === catKey) }))
      .filter((g) => g.items.length > 0);

  // Write an override change for a single page. value: true | false | 'default' (removes it).
  const setOverride = async (emp, pageKey, value) => {
    if (!canView) return;
    const cellId = `${emp.id}:${pageKey}`;
    setSavingCell(cellId);
    setError('');
    try {
      const ref = doc(db, 'users', emp.id);
      await updateDoc(ref, {
        [`pageOverrides.${pageKey}`]: value === 'default' ? deleteField() : value,
      });
      // Reflect locally without a refetch.
      setEmployees((prev) =>
        prev.map((e) => {
          if (e.id !== emp.id) return e;
          const nextOverrides = { ...(e.pageOverrides || {}) };
          if (value === 'default') delete nextOverrides[pageKey];
          else nextOverrides[pageKey] = value;
          return { ...e, pageOverrides: nextOverrides };
        })
      );
    } catch (err) {
      console.error('Error saving override:', err);
      setError(t.saveFailed);
    } finally {
      setSavingCell(null);
    }
  };

  if (!canView) {
    return (
      <div className={styles.pageContainer} dir={isRTL ? 'rtl' : 'ltr'}>
        <div className={styles.errorMessage}>{t.noPermission}</div>
      </div>
    );
  }

  const renderOverrideEditor = (emp) => (
    <div className={styles.editor}>
      <p className={styles.editHint}>{t.editHint}</p>
      {groupByCategory(PAGE_CATALOG).map(({ catKey, items }) => (
        <div key={catKey} className={styles.pageCatGroup}>
          <div className={styles.pageCatTitle}>{CATEGORIES[catKey][language]}</div>
          {items.map((p) => {
            const ov = emp.pageOverrides ? emp.pageOverrides[p.key] : undefined;
            const state = ov === true ? 'allow' : ov === false ? 'deny' : 'default';
            const roleDefault = roleCanAccess(p, emp.role || '');
            const cellId = `${emp.id}:${p.key}`;
            const isSaving = savingCell === cellId;
            return (
              <div key={p.key} className={styles.editorRow}>
                <div className={styles.editorPageInfo}>
                  <span className={styles.pageNamePlain}>{p[language]}</span>
                  <span className={roleDefault ? styles.tinyAllowed : styles.tinyDenied}>
                    {roleDefault ? t.byRoleAllowed : t.byRoleDenied}
                  </span>
                </div>
                <div className={styles.segmented} role="group">
                  <button
                    type="button"
                    className={`${styles.segBtn} ${state === 'default' ? styles.segActive : ''}`}
                    onClick={() => setOverride(emp, p.key, 'default')}
                    disabled={isSaving}
                  >
                    {t.optDefault}
                  </button>
                  <button
                    type="button"
                    className={`${styles.segBtn} ${state === 'allow' ? styles.segActiveAllow : ''}`}
                    onClick={() => setOverride(emp, p.key, true)}
                    disabled={isSaving}
                  >
                    {t.optAllow}
                  </button>
                  <button
                    type="button"
                    className={`${styles.segBtn} ${state === 'deny' ? styles.segActiveDeny : ''}`}
                    onClick={() => setOverride(emp, p.key, false)}
                    disabled={isSaving}
                  >
                    {t.optDeny}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );

  return (
    <div className={styles.pageContainer} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className={styles.header}>
        <div>
          <h2 className={styles.title}>{t.pageTitle}</h2>
          <p className={styles.subtitle}>{t.subtitle}</p>
        </div>
        <LanguageToggle language={language} onToggle={toggleLanguage} />
      </div>

      <div className={styles.viewTabs}>
        <button
          className={`${styles.viewTab} ${view === 'employee' ? styles.viewTabActive : ''}`}
          onClick={() => setView('employee')}
        >
          {t.viewByEmployee}
        </button>
        <button
          className={`${styles.viewTab} ${view === 'role' ? styles.viewTabActive : ''}`}
          onClick={() => setView('role')}
        >
          {t.viewByRole}
        </button>
      </div>

      {error && <div className={styles.errorMessage}>{error}</div>}

      {view === 'employee' && (
        <>
          <div className={styles.filtersRow}>
            <input
              type="text"
              className={styles.searchInput}
              placeholder={t.searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <div className={styles.filterGroup}>
              <label>{t.roleFilter}</label>
              <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
                <option value="all">{t.allRoles}</option>
                {rolesPresent.map((r) => (
                  <option key={r} value={r}>{roleLabel(r, language)}</option>
                ))}
              </select>
            </div>
            <div className={styles.filterGroup}>
              <label>{t.statusFilter}</label>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="active">{t.active}</option>
                <option value="terminated">{t.terminated}</option>
                <option value="all">{t.allStatuses}</option>
              </select>
            </div>
          </div>

          {loading ? (
            <div className={styles.loadingIndicator}>{t.loading}</div>
          ) : filteredEmployees.length === 0 ? (
            <div className={styles.noData}>{t.noEmployees}</div>
          ) : (
            <div className={styles.cardsGrid}>
              {filteredEmployees.map((emp) => {
                const role = emp.role || '';
                const accessible = accessiblePagesFor(emp);
                const terminated = isTerminated(emp);
                const isOpen = expandedId === emp.id;
                const isEditing = editingId === emp.id;
                return (
                  <div key={emp.id} className={`${styles.card} ${isEditing ? styles.cardEditing : ''}`}>
                    <div className={styles.cardHead}>
                      <div className={styles.cardIdentity}>
                        <div className={styles.cardName}>{emp.name || emp.email || '—'}</div>
                        <div className={styles.cardEmail}>{emp.email || '—'}</div>
                      </div>
                      <span className={`${styles.statusBadge} ${terminated ? styles.statusTerminated : styles.statusActive}`}>
                        {terminated ? t.terminated : t.active}
                      </span>
                    </div>
                    <div className={styles.cardMeta}>
                      <span className={styles.roleChip}>{role ? roleLabel(role, language) : t.noRole}</span>
                      <span className={styles.pagesCount}>{t.pagesCount(accessible.length)}</span>
                    </div>

                    <div className={styles.cardActions}>
                      <button
                        className={styles.togglePages}
                        onClick={() => { setExpandedId(isOpen ? null : emp.id); if (isEditing) setEditingId(null); }}
                      >
                        {isOpen ? t.hidePages : t.showPages}
                      </button>
                      <button
                        className={`${styles.togglePages} ${isEditing ? styles.editActive : ''}`}
                        onClick={() => { setEditingId(isEditing ? null : emp.id); if (!isEditing) setExpandedId(null); }}
                      >
                        {isEditing ? t.doneEditing : t.editAccess}
                      </button>
                    </div>

                    {isOpen && !isEditing && (
                      <div className={styles.pagesList}>
                        {groupByCategory(accessible).map(({ catKey, items }) => (
                          <div key={catKey} className={styles.pageCatGroup}>
                            <div className={styles.pageCatTitle}>{CATEGORIES[catKey][language]}</div>
                            <ul>
                              {items.map((p) => {
                                const ov = emp.pageOverrides ? emp.pageOverrides[p.key] : undefined;
                                return (
                                  <li key={p.key}>
                                    <span className={styles.pageName}>{p[language]}</span>
                                    {ov === true && <span className={styles.overrideTag}>{t.overrideAllowed}</span>}
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        ))}
                      </div>
                    )}

                    {isEditing && renderOverrideEditor(emp)}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {view === 'role' && (
        <div className={styles.matrixWrapper}>
          <p className={styles.subtitle}>{t.roleColumnNote}</p>
          <div className={styles.tableScroll}>
            <table className={styles.matrixTable}>
              <thead>
                <tr>
                  <th className={styles.stickyCol}>{t.colAccess}</th>
                  {ALL_ROLES.map((r) => (
                    <th key={r}>{roleLabel(r, language)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {groupByCategory(PAGE_CATALOG).map(({ catKey, items }) => (
                  <React.Fragment key={catKey}>
                    <tr className={styles.catRow}>
                      <td className={styles.stickyCol} colSpan={ALL_ROLES.length + 1}>
                        {CATEGORIES[catKey][language]}
                      </td>
                    </tr>
                    {items.map((p) => (
                      <tr key={p.key}>
                        <td className={styles.stickyCol}>{p[language]}</td>
                        {ALL_ROLES.map((r) => (
                          <td key={r} className={styles.matrixCell}>
                            {roleCanAccess(p, r) ? <span className={styles.checkYes}>✓</span> : <span className={styles.checkNo}>—</span>}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamAccessPage;
